"use client";

import { FormulaExpression } from "../formula/FormulaExpression";

/** Read-only metric-definition review before downstream ROI modelling. */
export interface MetricDefinitionReviewItem {
  id: string;
  name?: string;
  definition?: string;
  calculation?: string;
  unit?: string;
  variables?: string[];
}

export interface MetricDefinitionReviewProps {
  metrics: MetricDefinitionReviewItem[];
}

function hasText(value: string | undefined): value is string {
  return Boolean(value?.trim());
}

function Placeholder({
  field,
  article = "a",
}: {
  field: string;
  article?: "a" | "an";
}) {
  return (
    <span className="italic text-(--fg-muted)">
      Please provide {article} {field}
    </span>
  );
}

/** Shows the editable metric fields and makes missing metadata explicit. */
export function MetricDefinitionReview({
  metrics,
}: MetricDefinitionReviewProps) {
  return (
    <div className="space-y-3">
      {metrics.map((metric, index) => {
        const headingId = `metric-definition-review-${index}`;

        return (
          <article
            key={metric.id}
            aria-labelledby={headingId}
            className="rounded-(--radius-container) border border-(--border-default) bg-(--bg-white) p-4 shadow-(--shadow-xs)"
          >
            <h2
              id={headingId}
              className="text-[13.5px] font-semibold text-(--fg-primary)"
            >
              {hasText(metric.name) ? (
                metric.name
              ) : (
                <Placeholder field="name" />
              )}
            </h2>

            <dl className="mt-3 space-y-3 border-t border-(--border-subtle) pt-3">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-(--fg-muted)">
                  Definition
                </dt>
                <dd className="mb-0 mt-1 text-[13px] leading-normal text-(--fg-primary)">
                  {hasText(metric.definition) ? (
                    metric.definition
                  ) : (
                    <Placeholder field="definition" />
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-(--fg-muted)">
                  Unit
                </dt>
                <dd className="mb-0 mt-1 text-[13px] text-(--fg-secondary)">
                  {hasText(metric.unit) ? (
                    metric.unit
                  ) : (
                    <Placeholder field="unit" />
                  )}
                </dd>
              </div>
              <div>
                <dt className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.07em] text-(--fg-muted)">
                  Calculation
                  <span
                    aria-hidden="true"
                    className="normal-case tracking-normal"
                  >
                    ƒx
                  </span>
                </dt>
                <dd className="mb-0 mt-1 text-[13px] font-medium leading-normal text-(--fg-primary)">
                  {hasText(metric.calculation) ? (
                    <FormulaExpression expression={metric.calculation} />
                  ) : (
                    <Placeholder article="an" field="equation" />
                  )}
                </dd>
              </div>
            </dl>
          </article>
        );
      })}
    </div>
  );
}
